/* eslint-disable max-len */
import { Box, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useContext, useState } from 'react';
import { IoSearch } from 'react-icons/io5';
import { LanguageContext } from '../../../../../../../../../../../../libs/Context';
import MainCard from '../../../../../../../../../../../../UiComponents/Cards/MainCard';
import CreatePlaylist from './CreatePlaylist';
import PlaylistTreeView from './PlaylistTreeView';
import useDebounce from '../../../../../../../../../../../../components/Hooks/useDebounce';

interface ThumbnailData {
	url: string;
	canEdit?: boolean;
	canDelete?: boolean;
	source?: string;
	key?: string;
}

interface PlaylistData {
	name?: string;
	description?: string;
	thumbnail?: ThumbnailData;
}

interface PlaylistNode {
	_id: string;
	name: string;
	contentCount?: number;
	videos?: any[];
}

interface SelectedPlaylist {
	playlistId: string;
	orderInPlaylist: number;
}

interface PlaylistSliderProps {
	onChange?: (playlists: any[] | any) => void;
	data?: PlaylistData;
	selectedPlaylists?: SelectedPlaylist[];
}

interface PlaylistState {
	inputValue: string;
	description: string;
	isCreating: boolean;
	createdPlaylist: string | null;
	hover: boolean;
	uploadDialogOpen: boolean;
	deleteDialogOpen: boolean;
	thumbnail: ThumbnailData;
	currentSelectedPlaylists: any;
	selectedPlaylistIds: string[];
	searchQuery: string;
	createCount: number;
}

const PlaylistSlider: React.FC<PlaylistSliderProps> = ({ onChange, data, selectedPlaylists = [] }) =>
{
	const { language } = useContext(LanguageContext);
	const [ searchQuery, setSearchQuery ] = useState('');
	const debouncedSearch = useDebounce(searchQuery, 500);

	// Initialize state with pre-selected playlists
	const [ state, setState ] = useState<any>({
		currentSelectedPlaylists : [], // Will be populated when TreeView loads
		selectedPlaylistIds      : selectedPlaylists.map((p) => p.playlistId),
		createCount              : 0
	});

	const {
		currentSelectedPlaylists,
		selectedPlaylistIds,
		createCount
	} = state;

	// Helper function to update state partially
	const updateState = useCallback((newState: Partial<PlaylistState>) =>
	{
		setState((prev) => ({ ...prev, ...newState }));
	}, []);

	// Enhanced handlePlaylistSelection to maintain orderInPlaylist
	const handlePlaylistSelection = useCallback((selectedIds: string[], selectedData?: PlaylistNode[]) =>
	{
		const updatedPlaylists = selectedData?.map((playlist) =>
		{
			// Check if this playlist was previously selected to maintain its order
			const existingPlaylist = currentSelectedPlaylists.find((p) => p.playlistId === playlist._id);

			return {
				...playlist,
				orderInPlaylist : existingPlaylist?.orderInPlaylist || 0
			};
		}) || [];

		updateState({
			selectedPlaylistIds      : selectedIds,
			currentSelectedPlaylists : updatedPlaylists
		});

		// Pass selected playlists to parent with proper format
		if (onChange)
		{
			const formattedPlaylists = updatedPlaylists.map((playlist) => ({
				playlistId      : playlist._id,
				orderInPlaylist : playlist.orderInPlaylist || 0
			}));

			onChange(formattedPlaylists);
		}
	}, [ onChange, updateState, currentSelectedPlaylists ]);

	const handleCreatePlaylist = useCallback((newPlaylistData) =>
	{
		// Extract the new playlist ID from the response
		if (newPlaylistData && newPlaylistData.length > 0)
		{
			const newPlaylist = newPlaylistData[newPlaylistData.length - 1];
			const newPlaylistId = newPlaylist._id || newPlaylist.playlistId;

			// Add the new playlist to selected IDs if it's not already selected
			if (newPlaylistId && !selectedPlaylistIds.includes(newPlaylistId))
			{
				const updatedSelectedIds = [ ...selectedPlaylistIds, newPlaylistId ];

				// Update current selected playlists to include the new one
				const updatedPlaylists = [ ...currentSelectedPlaylists, {
					...newPlaylist,
					orderInPlaylist : currentSelectedPlaylists.length
				} ];

				updateState({
					selectedPlaylistIds      : updatedSelectedIds,
					currentSelectedPlaylists : updatedPlaylists,
					createCount              : createCount + 1
				});

				// Pass to parent component with proper format
				if (onChange)
				{
					const formattedPlaylists = updatedPlaylists.map((playlist) => ({
						playlistId      : playlist._id,
						orderInPlaylist : playlist.orderInPlaylist || 0
					}));

					onChange(formattedPlaylists);
				}
			}
			else
			{
				updateState({ createCount: createCount + 1 });
			}
		}
		else
		{
			updateState({ createCount: createCount + 1 });
		}
	}, [ updateState, createCount, selectedPlaylistIds, onChange, currentSelectedPlaylists ]);

	return (
		<Grid container width={'100%'}>
			<CreatePlaylist data={data} onChange={onChange} handlCreateCallback={handleCreatePlaylist}/>
			<Box width={'100%'} pt={2} pb={1}>
				<MainCard sx={{ width: '100%', maxHeight: '350px', overflowY: 'auto' }}>
					<Box position={'sticky'} top={'0'} sx={{ background: 'var(--whiteColor)', zIndex: 9 }}>
						<Typography variant='body2' color={'#333333'} pb={0.5}>{language?.addToplaylist}</Typography>
						<Box width={'100%'} py={1}>
							<TextField
								fullWidth
								size='small'
								placeholder={language?.searchPlaylist}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								sx={{
									backgroundColor            : '#F4F4F4',
									borderRadius               : '4px',
									'& .MuiOutlinedInput-root' : {
										border       : 'none',
										'& fieldset' : {
											border : 'none'
										},
										'&:hover fieldset' : {
											border : 'none'
										},
										'&.Mui-focused fieldset' : {
											border : 'none'
										}
									},
									'& .MuiInputBase-root' : {
										boxShadow : 'none'
									}
								}}
								InputProps={{
									startAdornment : (
										<InputAdornment position='start'>
											<IoSearch />
										</InputAdornment>
									)
								}}
							/>
						</Box>
					</Box>
					<PlaylistTreeView
						onSelect={handlePlaylistSelection}
						initialSelectedIds={selectedPlaylistIds}
						searchQuery={debouncedSearch}
						key={createCount}
					/>
				</MainCard>
			</Box>

			{/* Upload Dialog for selecting thumbnails */}
		</Grid>
	);
};

export default PlaylistSlider;